import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { fromIsoToTimestamp } from 'utils/converters/time/from.util';
import flatMap from 'utils/functions/flatMap.util';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import nullBind from 'utils/functions/nullBind.util';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';

const L = logger('determinePriceLines');

const fromServiceToPriceLine = (item) => ({
  serviceId: item.id || item.serviceId,
  stylistId: item.stylistId,
  amount: item.finalPrice,
  price: item.price,
  finalPrice: item.finalPrice,
  discount: item.discount,
  discountType: item.discountType,
  start: fromIsoToTimestamp(item.start_time) || 0,
  appointmentId: item.appointmentId,
  quantity: 1,
});

const fromProductToPriceLine = (item) => ({
  productId: item.id || item.productId,
  stylistId: item.stylistId,
  amount: item.finalPrice,
  price: item.price,
  finalPrice: item.finalPrice,
  discount: item.discount,
  discountType: item.discountType,
  start: Number.MIN_SAFE_INTEGER,
  description: item.title,
  quantity: item.quantity,
  totalPrice: item.totalPrice,
});

const fromClientToPriceLines = (client) => {
  if (!client) {
    // makes sure if empty array, it is always the same instance - helps in keeping down the number of re-renders
    return asArray();
  }

  const services = asArray(client.services).filter(alwaysSame);
  const products = asArray(client.products).filter(alwaysSame);

  const clientId = client.id;
  const withClientId = (item) => ({ ...item, clientId });

  return services.length || products.length ? [...services.map(fromServiceToPriceLine), ...products.map(fromProductToPriceLine)].map(withClientId) : asArray();
};

const fromClientsToPriceLines = nullBind(flatMap, fromClientToPriceLines);

const fromDataToPriceLines = (data) => {
  if (!data) {
    // helps in keeping down the number of re-renders if empty array is always the same instance
    return asArray();
  }

  const priceLines = fromClientsToPriceLines(asArray(data.clients).filter(alwaysSame));

  return priceLines.length ? priceLines : asArray();
};

const fromAppointmentToServiceInfos = (appointment) => {
  if (!appointment) {
    // helps in keeping down the number of re-renders if empty array is always the same instance
    return asArray();
  }

  return asArray(appointment.procedures)
    .filter(alwaysSame)
    .filter((p) => p.service)
    .map(({ service: { id, name } }) => ({
      serviceId: id,
      description: name,
    }));
};

const determinePriceLines = (options) => {
  const { data, appointment, serviceMap, t } = options ?? {};

  const priceLines = fromDataToPriceLines(data);
  const serviceInfos = fromAppointmentToServiceInfos(appointment);

  if (!isFullArray(priceLines) && !isFullArray(serviceInfos)) {
    // helps in keeping down the number of re-renders if empty array is always the same instance
    return asArray();
  }

  const sortedPriceLines = asArray(priceLines)
    .map((priceLine) => ({ ...priceLine, ...serviceInfos.find(({ serviceId }) => serviceId === priceLine.serviceId) }))
    .sort((a, b) => a.start < b.start);

  // this is a fix for a missing service name which will make the description empty
  // use store's salonInfo.serviceMap to fill it in or default it to some non-empty string
  for (const line of sortedPriceLines) {
    const { description, serviceId } = line;
    if (fromAnyToTrimmed(description)) {
      continue; // eslint-disable-line no-continue
    }

    L.warn('()', 'Missing description:', description, 'in price line:', line);

    if (!serviceId) {
      L.error('()', 'Invalid serviceId:', serviceId, 'for missing description:', description, 'in price line:', line);
    }

    const service = serviceMap?.[serviceId];
    if (!service) {
      L.error('()', 'Missing service:', service, ' for serviceId:', serviceId, 'in serviceMap:', serviceMap);
    }

    const { categoryName, name: serviceName } = service ?? {};
    line.description = fromAnyToTrimmed(serviceName) || fromAnyToTrimmed(categoryName) || t('composite.determinePriceLines.defaultPriceLine');
  }

  return sortedPriceLines;
};

export default determinePriceLines;
